import { useCallback, useContext, useEffect, useRef, useMemo } from 'react';

import { IEventProperties } from '~/services/analytics/analytics.service';
import {
    AnalyticsBoundaryContext,
    IAnalyticsBoundaryData
} from '~/components/Analytics/AnalyticsBoundary.provider';
import { useIsInAppBrowser } from './useIsInAppBrowser';
import { useAuthenticatedGlobalContext } from '~/stores/useAuthenticatedGlobalContext';
import { shouldCalculateAppPath } from '~/services/analytics/analytics.service.utils';

export type TTrackFunction = ReturnType<typeof useAnalytics>['track'];

type TTrackFn = (eventName: string, properties?: IEventProperties) => void;
type TTrackWithoutActionFn = (
    eventName: string,
    properties?: Omit<IEventProperties, 'action'>
) => void;

export const useDataAnalytics = () => {
    const analytics = useAnalytics();

    const {
        userStore: { currUser },
        configStore
    } = useAuthenticatedGlobalContext();

    const fireEvent = useCallback(
        (eventName, properties = {}) => {
            const props = {
                eventName,
                ...properties,
                userId: currUser?._id
            };
            const queryString = new URLSearchParams(props).toString();

            const url = `https://etp.honeybook.com/etp.png?${queryString}`;
            const fetchParams: RequestInit = {
                method: 'get',
                mode: 'no-cors',
                cache: 'no-cache' as RequestCache
            };
            if (configStore.isProduction()) {
                fetch(url, fetchParams);
            } else {
                // eslint-disable-next-line no-console
                console.log(props);
            }
        },
        [configStore, currUser?._id]
    );

    const track = useCallback<TTrackFn>(
        (eventName, properties = {}) => {
            fireEvent(eventName, properties);
            analytics.track(eventName, properties);
        },
        [analytics, fireEvent]
    );

    const trackClick = useCallback<TTrackWithoutActionFn>(
        (eventName, properties = {}) => {
            fireEvent(eventName, { action: 'click', ...properties });
            analytics.trackClick(eventName, properties);
        },
        [analytics, fireEvent]
    );

    const trackLoaded = useCallback<TTrackWithoutActionFn>(
        (eventName, properties = {}) => {
            fireEvent(eventName, { action: 'loaded', ...properties });
            analytics.trackLoaded(eventName, properties);
        },
        [analytics, fireEvent]
    );

    return useMemo(
        () => ({
            track,
            trackClick,
            trackLoaded
        }),
        [track, trackClick, trackLoaded]
    );
};

export default function useAnalytics() {
    const { analyticsService, fullstoryService } =
        useAuthenticatedGlobalContext();
    // In order to calculate the app path only once and not in each render.
    const { getBoundariesFullData } = useContext(AnalyticsBoundaryContext);
    const appPathRef = useRef<IAnalyticsBoundaryData | null>(
        getBoundariesFullData?.() || null
    );
    const isInAppBrowser = useIsInAppBrowser();

    useEffect(() => {
        if (shouldCalculateAppPath(appPathRef.current, getBoundariesFullData)) {
            return;
        }

        appPathRef.current = getBoundariesFullData();
    }, [appPathRef, getBoundariesFullData]);

    const track = useCallback<TTrackFn>(
        (eventName, properties = {}) => {
            const analyticsBoundariesProps = appPathRef.current
                ? appPathRef.current.properties
                : {};

            fullstoryService.trackFullstoryEvent({
                event: eventName,
                props: properties
            });

            return analyticsService.track(eventName, {
                app_path: appPathRef.current && appPathRef.current.name,
                is_in_app_browser: isInAppBrowser,
                ...analyticsBoundariesProps,
                ...properties
            });
        },
        [analyticsService, isInAppBrowser, fullstoryService]
    );

    const trackClick = useCallback<TTrackWithoutActionFn>(
        (eventName, properties = {}) => {
            return track(eventName, { ...properties, action: 'click' });
        },
        [track]
    );

    const trackLoaded = useCallback<TTrackWithoutActionFn>(
        (eventName, properties = {}) => {
            return track(eventName, { ...properties, action: 'loaded' });
        },
        [track]
    );

    return useMemo(
        () => ({
            track,
            trackClick,
            trackLoaded
        }),
        [track, trackClick, trackLoaded]
    );
}
